import { useEffect, useEffectEvent, useState } from "react";

import { BRAND_CONFIGURATION } from "../../../../../configs/brands";
import { useOverlayOrchestrator } from "../../createOverlayOrchestratorContext";
import { useOverlayStepsOrchestrator } from "../../useOverlayStepsOrchestrator";

declare global {
  interface Window {
    QSI?: QSI;
  }
}

export type QSI = {
  API: {
    getIntercept: (id: string) => Promise<{
      getEvaluationResult: () => {
        passed: boolean;
        creative: {
          type:
            | "Empty"
            | "EmbeddedFeedback"
            | "WebResponsiveDialog"
            | "FeedbackButton"
            | "FeedbackLink"
            | "IFrame"
            | "InfoBar"
            | "Link"
            | "NoCreative"
            | "PopOver"
            | "PopUnder"
            | "PopUnderHelper"
            | "Slider"
            | "UserDefinedHTML";
        };
      };
      onClose: (callback: () => void) => void;
    }>;
  };
  global: {
    intercepts: Record<string, unknown>;
  };
};

const { qualtrics } = BRAND_CONFIGURATION;
const KEY = "qualtrics";
/**
 * Only these creative types are handled by the Overlay Orchestrator as they are triggered without user interaction.
 * This type list is based on Qualtrics documentation:
 * https://api.qualtrics.com/7023b0cd7acdd-website-feedback-java-script-api#creativetype
 */
const INCLUDE_QUALTRICS_CREATIVE_TYPES = ["WebResponsiveDialog", "PopOver"];

export const handleQualtricsIntercepts = async (
  intercepts: string[],
  QSI: QSI,
  onInterceptComplete: (id: string) => void,
) => {
  await Promise.all(
    intercepts.map(async (id) => {
      const interceptAPI = await QSI.API.getIntercept(id);
      const { passed, creative } = interceptAPI.getEvaluationResult();
      const { type } = creative;

      if (!passed || !INCLUDE_QUALTRICS_CREATIVE_TYPES.includes(type)) {
        onInterceptComplete(id);

        return;
      }

      interceptAPI.onClose(() => onInterceptComplete(id));
    }),
  );
};

export const useInitQualtrics = () => {
  const { dispatch } = useOverlayOrchestrator();
  const canProceed = useOverlayStepsOrchestrator(KEY);

  /**
   * State to track pending intercepts
   * - null: not initialized
   * - string[]: list of pending intercept IDs
   */
  const [pendingIntercepts, setPendingIntercepts] = useState<string[] | null>(
    null,
  );

  /**
   * Callback to remove a pending intercept from the state
   */
  const removePendingIntercept = useEffectEvent((id: string) => {
    setPendingIntercepts((prevState) =>
      Array.isArray(prevState)
        ? [...prevState.filter((value) => value !== id)]
        : prevState,
    );
  });

  /**
   * Listen to Qualtrics JS Loaded Event
   * https://api.qualtrics.com/7023b0cd7acdd-website-feedback-java-script-api
   */
  const listenInterceptsEvent = useEffectEvent(() => {
    window.addEventListener(
      "qsi_js_loaded",
      async () => {
        if (!window.QSI) {
          dispatch({ type: "DISABLE", payload: KEY });

          return;
        }

        const intercepts = Object.keys(window.QSI.global.intercepts);

        if (intercepts.length === 0) {
          dispatch({ type: "DISABLE", payload: KEY });

          return;
        }

        // Set pending intercepts to manage their completion
        setPendingIntercepts(intercepts);

        // Listen and handle Qualtrics intercepts
        await handleQualtricsIntercepts(
          intercepts,
          window.QSI,
          removePendingIntercept,
        );
      },
      { once: true, passive: true },
    );
  });

  useEffect(() => {
    if (!qualtrics) {
      dispatch({ type: "DISABLE", payload: KEY });

      return;
    }

    if (canProceed) {
      listenInterceptsEvent();

      dispatch({ type: "INIT", payload: KEY });
    }
  }, [canProceed, dispatch]);

  useEffect(() => {
    if (!Array.isArray(pendingIntercepts)) return;

    if (pendingIntercepts.length === 0) {
      dispatch({ type: "COMPLETE", payload: KEY });
    }
  }, [pendingIntercepts, dispatch]);
};
